import { useState, useEffect } from "react";
import Button from "./Button";
import { Checkbox } from "primereact/checkbox";
import useReports from "services/ReportService";
import { useToastContext } from "components/Toast";
import { useReportContext } from "context/ReportContext";
import { useNavigate, useLocation } from "react-router-dom";

export default function SectionsModal({
  type = "welcome",
  size = "normal",
  isOpen = false,
  closeModal,
  clients,
}) {
  const { report } = useReportContext();
  const { updateSections } = useReports();
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const performanceList = [
    {
      name: "Best Ranking Categories",
      key: "pd_best_ranking_categories",
    },
    {
      name: "Worst Ranking Categories",
      key: "pd_worst_ranking_categories",
    },
    {
      name: "Traffic Gain Opportunities",
      key: "pd_traffic_opps",
    },
    {
      name: "Biggest Revenue Gain Opportunities",
      key: "pd_revenue_opps",
    },
    {
      name: "Keyword Ranking Opportunities",
      key: "pd_ranking_opps",
    },
    {
      name: "Keywords Ranking Per URL",
      key: "pd_url_ranking",
    },
    {
      name: "Biggest Searched for Categories",
      key: "pd_biggest_search_categories",
    },
    {
      name: "Keywords Intent",
      key: "pd_keyword_intent",
    },
    {
      name: "Keywords Page Template",
      key: "pd_page_template",
    },
  ];

  const revenueList = [
    {
      name: "Traffic - Top 5 By Category",
      key: "rto_traffic_category",
    },
    {
      name: "Traffic - Top 5 By Sub-Category",
      key: "rto_traffic_sub",
    },
    {
      name: "Traffic - Top 5 By Type",
      key: "rto_traffic_type",
    },
    {
      name: "Revenue - Top 5 By Category",
      key: "rto_revenue_category",
    },
    {
      name: "Revenue - Top 5 By Sub-Category",
      key: "rto_revenue_sub",
    },
    {
      name: "Revenue - Top 5 By Type",
      key: "rto_revenue_type",
    },
  ];

  const uxList = [
    {
      name: "Category: Traffic, Bounce Rate, Exit Rate",
      key: "ux_category_tbe",
    },
    {
      name: "Category: Traffic, CVR",
      key: "ux_category_tcvr",
    },
    {
      name: "Page Template: Traffic, Bounce Rate, Exit Rate",
      key: "ux_template_tbe",
    },
    {
      name: "Page Template: Traffic, CVR",
      key: "ux_template_tcvr",
    },
  ];

  const competitorList = [
    {
      name: "Share of Estimated Yearly Traffic",
      key: "cp_traffic",
    },
    {
      name: "Keyword Gaps",
      key: "cp_keyword_gaps",
    },
    {
      name: "Competitor Rankings",
      key: "cp_comp_rankings",
    },
    {
      name: "Competitor Estimated Yearly Traffic",
      key: "cp_comp_traffic",
    },
    {
      name: "Competitor Estimated Yearly Revenue",
      key: "cp_comp_revenue",
    },
  ];

  const hcsList = [
    {
      name: "Total Monthly Searches",
      key: "kwtrends",
    },
    {
      name: "Searches Breakdown - Category",
      key: "hcs_cat_break",
    },
    {
      name: "Searches Breakdown - Sub-Category",
      key: "hcs_sub_cat_break",
    },
    {
      name: "Searches Breakdown - Type",
      key: "hcs_location_break",
    },
    {
      name: "Searches Year On Year - Category",
      key: "hcs_cat_yoy",
    },
    {
      name: "Searches Year On Year - Sub-Category",
      key: "hcs_sub_cat_yoy",
    },
    {
      name: "Searches Year On Year - Type",
      key: "hcs_location_yoy",
    },
  ];

  const ffList = [
    {
      name: "SERP Feature Result Type",
      key: "ff_type",
    },
    {
      name: "Owned SERP Features",
      key: "ff_owned",
    },
    {
      name: "Top SERP Features Owned By Competitor",
      key: "ff_competitor",
    },
  ];

  const [selectedSections, setSelectedSections] = useState([]);

  useEffect(() => {
    if (report.sections) {
      const keysToInclude = report.sections.split(",");
      const combinedList = [
        ...performanceList,
        ...revenueList,
        ...uxList,
        ...competitorList,
        ...hcsList,
        ...ffList,
      ];
      const filteredList = combinedList.filter((item) =>
        keysToInclude.includes(item.key)
      );

      setSelectedSections(filteredList);
    }
  }, [report.sections]);

  const onSectionChange = (e) => {
    let _selectedSections = [...selectedSections];

    if (!e.checked) _selectedSections.push(e.value);
    else
      _selectedSections = _selectedSections.filter(
        (section) => section.key !== e.value.key
      );

    setSelectedSections(_selectedSections);
  };

  const { showToast } = useToastContext();

  const handleApply = async () => {
    const response = await updateSections({
      id: report.id,
      sections: selectedSections.map((item) => item.key).join(","),
    });
    if (response.status === 200) {
      showToast({ severity: "success", summary: "Saved", life: 3000 });
      const path = location.pathname;

      if (path.includes("report-overview")) {
        closeModal();
        navigate(0);
      } else {
        closeModal();
        navigate(`/report/${report.id}/report-overview`);
        navigate(0);
      }
    }
  };

  const handleClose = async () => {
    closeModal(setSelectedSections);
    if (report.sections) {
      const keysToInclude = report.sections.split(",");
      const combinedList = [
        ...performanceList,
        ...revenueList,
        ...uxList,
        ...competitorList,
        ...hcsList,
        ...ffList,
      ];
      const filteredList = combinedList.filter((item) =>
        keysToInclude.includes(item.key)
      );

      setSelectedSections(filteredList);
    } else {
      setSelectedSections([]);
    }
  };

  const handleBackgroundClick = (e) => {
    if (e.target === e.currentTarget) {
      closeModal();
    }
  };

  if (isOpen) {
    return (
      <div className={"full-page-cover "} onClick={handleBackgroundClick}>
        <div
          className={(
            "welcome-popup modal " +
            (isLoading ? " loading" : "") +
            (size === "normal" ? " full-width" : "small")
          ).trim()}
        >
          <div className="modal-inner">
            <div className="modal-main">
              <div className="modal-title">
                <h2>Edit Sections</h2>
              </div>
              <div className="modal-main-inner">
                <div className="modal-main-inner-sections">
                  <div className="sections-section">
                    <h3>Performance Dashboard</h3>
                    <div className="sections-list">
                      {performanceList.map((category) => {
                        return (
                          <div key={category.key} className="sections-row">
                            <Checkbox
                              inputId={category.key}
                              onChange={onSectionChange}
                              value={category}
                              checked={
                                !selectedSections.some(
                                  (item) => item.key === category.key
                                )
                              }
                              name="section"
                              variant="small"
                            />
                            <label htmlFor={category.key} className="ml-2">
                              {category.name}
                            </label>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <div className="sections-section">
                    <h3>Revenue & Traffic Opportunity</h3>
                    <div className="sections-list">
                      {revenueList.map((category) => {
                        return (
                          <div key={category.key} className="sections-row">
                            <Checkbox
                              inputId={category.key}
                              onChange={onSectionChange}
                              value={category}
                              checked={
                                !selectedSections.some(
                                  (item) => item.key === category.key
                                )
                              }
                              name="section"
                              variant="small"
                            />
                            <label htmlFor={category.key} className="ml-2">
                              {category.name}
                            </label>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <div className="sections-section">
                    <h3>UX Dashboard</h3>
                    <div className="sections-list">
                      {uxList.map((category) => {
                        return (
                          <div key={category.key} className="sections-row">
                            <Checkbox
                              inputId={category.key}
                              onChange={onSectionChange}
                              value={category}
                              checked={
                                !selectedSections.some(
                                  (item) => item.key === category.key
                                )
                              }
                              name="section"
                              variant="small"
                            />
                            <label htmlFor={category.key} className="ml-2">
                              {category.name}
                            </label>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <div className="sections-section">
                    <h3>Competitor Performance</h3>
                    <div className="sections-list">
                      {competitorList.map((category) => {
                        return (
                          <div key={category.key} className="sections-row">
                            <Checkbox
                              inputId={category.key}
                              onChange={onSectionChange}
                              value={category}
                              checked={
                                !selectedSections.some(
                                  (item) => item.key === category.key
                                )
                              }
                              name="section"
                              variant="small"
                            />
                            <label htmlFor={category.key} className="ml-2">
                              {category.name}
                            </label>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <div className="sections-section">
                    <h3>How Customers Search</h3>
                    <div className="sections-list">
                      {hcsList.map((category) => {
                        return (
                          <div key={category.key} className="sections-row">
                            <Checkbox
                              inputId={category.key}
                              onChange={onSectionChange}
                              value={category}
                              checked={
                                !selectedSections.some(
                                  (item) => item.key === category.key
                                )
                              }
                              name="section"
                              variant="small"
                            />
                            <label htmlFor={category.key} className="ml-2">
                              {category.name}
                            </label>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <div className="sections-section">
                    <h3>Feature Format</h3>
                    <div className="sections-list">
                      {ffList.map((category) => {
                        return (
                          <div key={category.key} className="sections-row">
                            <Checkbox
                              inputId={category.key}
                              onChange={onSectionChange}
                              value={category}
                              checked={
                                !selectedSections.some(
                                  (item) => item.key === category.key
                                )
                              }
                              name="section"
                              variant="small"
                            />
                            <label htmlFor={category.key} className="ml-2">
                              {category.name}
                            </label>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <div className="close-modal">
                <Button
                  className={"cancel-button border"}
                  onClick={handleClose}
                  variant="secondary"
                >
                  Cancel
                </Button>
              </div>
              <Button onClick={handleApply}>Save</Button>
            </div>
          </div>
        </div>
      </div>
    );
  } else return;
}
